/**
 * "Tên miền dùng chủ thể này": the domains whose contacts were cloned from the subject, how each
 * compares with the subject now, and "Đồng bộ sang tên miền" for the ones staff choose. Editing
 * the subject changes no domain by itself.
 */

import { Globe2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../../api';
import type { ContactField, ContactSyncStatus, DomainSyncResult, SubjectDomainContact } from '../../api/areas/core';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useApi } from '../../lib/useApi';
import { DomainRef, RoleBadge, useVocab } from '../domains/refs';
import { changeGroups } from '../domains/tabs/contactSync';

interface DomainRow {
  domain_id: string;
  ascii_name: string;
  domain: string;
  contacts: SubjectDomainContact[];
  /** The domain's least up-to-date status. */
  status: ContactSyncStatus;
}

const RANK: Record<ContactSyncStatus, number> = { SYNCING: 3, EDITED: 2, BEHIND: 1, CURRENT: 0 };
const TONE = { CURRENT: 'ok', BEHIND: 'pending', EDITED: 'serious', SYNCING: 'info' } as const;

function group(items: SubjectDomainContact[]): DomainRow[] {
  const rows = new Map<string, DomainRow>();
  for (const c of items) {
    const row = rows.get(c.domain_id) ?? { domain_id: c.domain_id, ascii_name: c.ascii_name, domain: c.domain, contacts: [], status: 'CURRENT' as ContactSyncStatus };
    row.contacts.push(c);
    if (RANK[c.status] > RANK[row.status]) row.status = c.status;
    rows.set(c.domain_id, row);
  }
  return [...rows.values()];
}

export function SubjectDomainSync({ subjectId }: { subjectId: string }) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const result = useApi((signal) => api.listSubjectDomainContacts(subjectId, { signal }), [subjectId]);
  const rows = useMemo(() => (result.status === 'success' ? group(result.data.items) : []), [result]);
  // Default: the domains still on the subject's older details (not edited separately).
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const selected = picked ?? new Set(rows.filter((r) => r.status === 'BEHIND').map((r) => r.domain_id));
  const [open, setOpen] = useState(false);
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  const columns: Column<DomainRow>[] = [
    {
      key: 'pick',
      header: '',
      cell: (r) => (
        <input
          type="checkbox"
          aria-label={t('subject.sync.select', { domain: r.domain })}
          checked={selected.has(r.domain_id)}
          disabled={r.status === 'SYNCING' || r.status === 'CURRENT'}
          onChange={() => toggle(r.domain_id)}
        />
      ),
    },
    { key: 'domain', header: t('subject.sync.domain'), cell: (r) => <DomainRef ascii={r.ascii_name} unicode={r.domain} id={r.domain_id} tab="subject" /> },
    {
      key: 'contacts',
      header: t('subject.sync.contacts'),
      cell: (r) => (
        <span className="flex flex-wrap gap-1">
          {r.contacts.map((c) => (
            <span key={c.role} title={t(`subject.sync.statuses.${c.status}`)}>
              <RoleBadge role={c.role} />
            </span>
          ))}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('subject.status'),
      cell: (r) => (
        <Badge tone={TONE[r.status]} title={r.status}>
          {t(`subject.sync.statuses.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'changes',
      header: t('subject.sync.changes'),
      cell: (r) => {
        const groups = changeGroups(r.contacts);
        if (!groups.length) return <span className="text-muted-foreground">—</span>;
        const fields = (f: ContactField[]) => f.map((x) => t(`contacts.fields.${x}`)).join(', ');
        return groups.map((g) => (g.roles ? `${g.roles.map((x) => vocab('role', x)).join(', ')}: ${fields(g.fields)}` : fields(g.fields))).join('; ');
      },
    },
  ];

  return (
    <Section
      flush
      title={t('subject.sync.title')}
      description={t('subject.sync.description')}
      actions={
        <Button size="sm" disabled={selected.size === 0} onClick={() => setOpen(true)}>
          {selected.size ? t('subject.sync.actionCount', { count: selected.size }) : t('subject.sync.action')}
        </Button>
      }
    >
      {result.status === 'loading' ? (
        <Loading label={t('subject.loading')} rows={2} />
      ) : result.status === 'error' ? (
        <DataError error={result.error} onRetry={result.reload} title={t('subject.sync.loadFailed')} />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.domain_id} empty={<EmptyState title={t('subject.sync.empty')} icon={Globe2} />} />
      )}
      {open ? (
        <SyncDialog
          subjectId={subjectId}
          rows={rows.filter((r) => selected.has(r.domain_id))}
          roleLabel={(r) => vocab('role', r)}
          onClose={() => setOpen(false)}
          onDone={() => {
            setPicked(null);
            result.reload();
          }}
        />
      ) : null}
    </Section>
  );
}

function SyncDialog({
  subjectId,
  rows,
  roleLabel,
  onClose,
  onDone,
}: {
  subjectId: string;
  rows: DomainRow[];
  roleLabel: (role: string) => string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('domains');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { domain_ids: string[]; reason: string }, opts) => api.syncSubjectToDomains(subjectId, b, opts));
  const done = w.state.phase === 'done' ? w.state.result.results : null;
  const outcome = (r: DomainSyncResult) => {
    switch (r.outcome) {
      case 'STARTED':
        return t('subject.sync.results.STARTED', { roles: r.roles.map(roleLabel).join(', ') });
      case 'REGISTRANT_CHANGE':
        return t(`subject.sync.results.${r.request}`);
      case 'SKIPPED':
        return t('subject.sync.results.SKIPPED', { reason: r.reason });
      default:
        return t('subject.sync.results.UP_TO_DATE');
    }
  };
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('subject.sync.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('subject.sync.dialogDescription')}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void w.run({ domain_ids: rows.map((r) => r.domain_id), reason }).then((r) => r && onDone());
          }}
        >
          <ul className="grid gap-1 text-sm">
            {rows.map((r) => (
              <li key={r.domain_id} className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{r.domain}</span>
                {done ? (
                  <span className="text-muted-foreground">{outcome(done.find((x) => x.domain_id === r.domain_id) ?? { outcome: 'UP_TO_DATE', domain_id: r.domain_id })}</span>
                ) : (
                  <span className="text-muted-foreground">{t(`subject.sync.statuses.${r.status}`)}</span>
                )}
              </li>
            ))}
          </ul>
          {!done && rows.some((r) => r.status === 'EDITED') ? <Callout tone="warn">{t('subject.sync.editedWarning')}</Callout> : null}
          <Field label={t('actions.reason')} hint={t('actions.reasonHint')}>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required disabled={Boolean(done)} />
          </Field>
          {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('actions.close')}
            </Button>
            <Button type="submit" disabled={w.busy || Boolean(done) || !reason.trim() || rows.length === 0}>
              {t('subject.sync.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
